import type { FC } from "react";
import { useRef, useCallback, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch } from "react-redux";
import styled from "styled-components";

import { Button, IconButton, ChartTooltip, PopupClickContainer } from "@similarweb/ui-components";
import type { ColumnDefinition } from "@similarweb/ui-components";

import { showSuccessToast } from "app/actions/toast_actions";
import { translate } from "app/filters/i18nFilter";
import { minAbbrNumberFilter, percentageSignFilter } from "app/filters/ngFilters";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { DefaultFetchService, requestParams } from "app/services/fetchService";

import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { EKWGeneratorTableTabs } from "app/common/keyword-research/keyword-generator/types";
import { KeywordsGroupEditorModal } from "app/pages/keyword-analysis/KeywordsGroupEditorModal";
import {
    GOOGLE_KEYWORD_GENERATOR_ENDPOINT,
    TABLE_TYPES_FOR_ENDPOINT,
} from "app/common/keyword-research/keyword-generator/constants";

import { SearchKeywordCell } from "./SearchKeywordCell";
import type { ISearchKeywordCellProps } from "./SearchKeywordCell";
import { allTrackers } from "app/services/track/track";

const TooltipFooter = styled(FlexRow)`
    justify-content: space-between;
`;

const ChartTooltipStyled = styled(ChartTooltip)`
    height: 252px;
` as typeof ChartTooltip;

interface DataRowType {
    keyword: string;
    volume: string;
    change: { value: string; isDecrease: boolean; unsigned: boolean };
}

const tooltipColumnDefinition: Array<ColumnDefinition<DataRowType>> = [
    {
        key: "0",
        get title() {
            return translate("search.keyword.cell.with.auto.create.tooltip.table.keyword.title");
        },
        selector: "keyword" as const,
        align: "left",
        size: "154px",
        borderRight: true,
        render: ({ keyword }) => <ChartTooltip.LabelCell label={keyword} />,
    },
    {
        key: "1",
        get title() {
            return translate("search.keyword.cell.with.auto.create.tooltip.table.volume.title");
        },
        selector: "volume" as const,
        align: "right",
        size: "100px",
        borderRight: true,
        render: ({ volume }) => <ChartTooltip.ValueCell value={volume} />,
    },
    {
        key: "2",
        get title() {
            return translate("search.keyword.cell.with.auto.create.tooltip.table.change.title");
        },
        selector: "change" as const,
        align: "right",
        size: "100px",
        render: ({ change }) => (
            <ChartTooltip.ChangeCell
                value={change.value}
                isDecrease={change.isDecrease}
                unsigned={change.unsigned}
            />
        ),
    },
];

export const SearchKeywordCellWithAutoCreate: FC<Omit<ISearchKeywordCellProps, "customCTA">> = (props) => {
    const { params } = useCurrentStateAndParams();
    const popupRef = useRef<PopupClickContainer>(null);
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const getData = useCallback(async () => {
        const { from, to } = DurationService.getDurationData(BasicDurations.LAST_TWENTY_EIGHT_DAYS).forAPI;
        const apiParams = {
            keyword: props.value,
            country: params.country,
            from,
            to,
            latest: "28d",
            isWindow: true,
            websource: "Total",
            sort: "score",
            asc: false,
            rowsPerPage: 5,
            type: TABLE_TYPES_FOR_ENDPOINT.related,
        };
        const paramsAsString = requestParams(apiParams);
        try {
            const response = await DefaultFetchService.getInstance().post<any>(
                `${GOOGLE_KEYWORD_GENERATOR_ENDPOINT}?${paramsAsString}`,
            );
            const records = response.records;
            return records.map((record: FixmeType) => ({
                keyword: record.keyword,
                volume: minAbbrNumberFilter()(record.windowVolume),
                change: {
                    value: percentageSignFilter()(record.volumeChange, 1),
                    isDecrease: record.volumeChange < 0,
                    unsigned: record.volumeChangePresentation === "-",
                },
            }));
        } catch (e) {
            popupRef.current?.closePopup();
        }
    }, [params.country, params.duration, props.value]);

    const navigateToKeywordGenerator = (keyword = props.value) => {
        allTrackers.dispatchTrackEvent("Button", "click", "See more keywords");
        proNavigator.go("digitalsuite_findkeywords_keywordGeneratorTool", {
            ...params,
            keyword,
            duration: BasicDurations.LAST_TWENTY_EIGHT_DAYS,
            tab: EKWGeneratorTableTabs.relatedKeywords,
        });
    };

    const showListCreatedSuccessfullyToast = (savedList: IUserKeywordGroup) => {
        dispatch(showSuccessToast(translate("create.list.wizard.toast", { listName: savedList.Name })));
    };

    const onSavedList = (savedList: IUserKeywordGroup) => {
        showListCreatedSuccessfullyToast(savedList);
        navigateToKeywordGenerator(`*${savedList.Id}`);
    };

    const onCreateKeywordGroupModalOpen = () => {
        setIsOpen(true);
        allTrackers.dispatchTrackEvent("Button", "click", "Create New Keyword Group/Tooltip");
    };

    const onPopupClick = (isOpen: boolean) => {
        if (isOpen) {
            allTrackers.dispatchTrackEvent("Button", "click", "Create New Keyword Group/Wand");
        }
    };

    return (
        <>
            <SearchKeywordCell
                {...props}
                customCTA={
                    <PopupClickContainer
                        ref={popupRef}
                        content={() => (
                            <ChartTooltipStyled
                                width={420}
                                columnDefinition={tooltipColumnDefinition}
                                title={translate("search.keyword.cell.with.auto.create.tooltip.table.title")}
                                subtitle={translate("search.keyword.cell.with.auto.create.tooltip.table.subtitle")}
                                data={getData}
                                LoaderComponent={() => <ChartTooltip.Loader lineCount={6} />}
                            >
                                <TooltipFooter>
                                    <Button
                                        type="flat"
                                        onClick={() => navigateToKeywordGenerator()}
                                    >
                                        {translate(
                                            "search.keyword.cell.with.auto.create.tooltip.table.footer.see.more",
                                        )}
                                    </Button>
                                    <IconButton
                                        type="primary"
                                        iconName="wand"
                                        iconSize="xs"
                                        onClick={onCreateKeywordGroupModalOpen}
                                    >
                                        {translate(
                                            "search.keyword.cell.with.auto.create.tooltip.table.footer.generate",
                                        )}
                                    </IconButton>
                                </TooltipFooter>
                            </ChartTooltipStyled>
                        )}
                        config={{
                            placement: "right",
                            onToggle: onPopupClick,
                        }}
                        appendTo=".sw-layout-research"
                    >
                        <span>
                            <IconButton
                                iconName="wand"
                                type="flat"
                                iconSize="xs"
                            />
                        </span>
                    </PopupClickContainer>
                }
            />
            <KeywordsGroupEditorModal
                onClose={() => {
                    setIsOpen(false);
                }}
                open={isOpen}
                seedKeyword={props.value}
                onSave={onSavedList}
            />
        </>
    );
};
